import { useState } from 'react'
import { Pencil, Save, X } from 'lucide-react'

interface SourceTabProps {
  sourceText: string
  analysisRaw?: string
  sectionTitle: string
  sectionSourceText: string | Record<string, unknown>
  onUpdateSectionSource: (text: string) => void
}

interface AnalysisData {
  keyMessages?: string[]
  data?: string[]
  targetAudience?: string
  keywords?: string[]
}

function parseAnalysis(raw: string): AnalysisData {
  try {
    const jsonMatch = raw.match(/\{[\s\S]*\}/)
    return jsonMatch ? JSON.parse(jsonMatch[0]) : {}
  } catch {
    return {}
  }
}

// 섹션 데이터를 문자열로 변환 (객체인 경우 구조화된 텍스트로)
function formatSectionText(data: string | Record<string, unknown> | undefined): string {
  if (!data) return ''
  if (typeof data === 'string') return data

  // 객체인 경우 key-value 형태로 변환
  const lines: string[] = []
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      lines.push(`[${key}]`)
      value.forEach((item, i) => {
        if (typeof item === 'object') {
          lines.push(`  ${i + 1}. ${JSON.stringify(item)}`)
        } else {
          lines.push(`  • ${item}`)
        }
      })
    } else if (typeof value === 'object' && value !== null) {
      lines.push(`[${key}]`)
      lines.push(`  ${JSON.stringify(value)}`)
    } else {
      lines.push(`[${key}] ${value}`)
    }
  }
  return lines.join('\n')
}

export function SourceTab({ sourceText, analysisRaw, sectionTitle, sectionSourceText, onUpdateSectionSource }: SourceTabProps) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState('')

  const analysis = analysisRaw ? parseAnalysis(analysisRaw) : null

  // 섹션 소스 텍스트를 문자열로 정규화
  const formattedSectionText = formatSectionText(sectionSourceText)
  const displayText = formattedSectionText || sourceText || ''

  const startEdit = () => {
    setEditText(displayText)
    setEditing(true)
  }

  const saveEdit = () => {
    onUpdateSectionSource(editText)
    setEditing(false)
  }

  return (
    <div className="p-4 space-y-4">
      {/* Analysis summary for this section */}
      {analysis && (analysis.keyMessages?.length || analysis.keywords?.length) && (
        <div className="bg-primary/5 rounded-lg p-3 space-y-2">
          <h4 className="text-xs font-bold text-primary uppercase tracking-wide">소스 분석 요약</h4>
          {analysis.keyMessages && analysis.keyMessages.length > 0 && (
            <div>
              <span className="text-xs font-medium text-text-secondary">핵심 메시지</span>
              <ul className="mt-0.5 space-y-0.5">
                {analysis.keyMessages.map((msg, i) => (
                  <li key={i} className="text-xs text-text-primary">• {msg}</li>
                ))}
              </ul>
            </div>
          )}
          {analysis.keywords && analysis.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {analysis.keywords.map((kw, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary">{kw}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Section source text */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-text-secondary">
            {sectionTitle} 원문
          </h4>
          {!editing && (
            <button onClick={startEdit} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary" title="편집">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={12}
              className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-tertiary">{editText.length.toLocaleString()}자</span>
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="btn btn-ghost text-xs flex items-center gap-1">
                  <X className="w-3 h-3" /> 취소
                </button>
                <button onClick={saveEdit} className="btn btn-primary text-xs flex items-center gap-1">
                  <Save className="w-3 h-3" /> 저장
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-bg-elevated/50 rounded-lg p-3">
            <p className="text-sm whitespace-pre-wrap leading-relaxed text-text-primary">
              {displayText || '원문이 등록되지 않았습니다.'}
            </p>
            <p className="text-xs text-text-tertiary mt-2">{displayText.length.toLocaleString()}자</p>
          </div>
        )}
      </div>
    </div>
  )
}
